import React from 'react';
import {
  Card, CardActionArea, CardContent, CardMedia, Box, Typography,
  Chip, Stack, Tooltip,
} from '@mui/material';
import {
  Timer as TimerIcon, Star as StarIcon, School as SchoolIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LEVEL_COLORS = {
  beginner:     { bg: '#dcfce7', color: '#166534' },
  intermediate: { bg: '#fef9c3', color: '#854d0e' },
  advanced:     { bg: '#fee2e2', color: '#991b1b' },
};

function getLevelStyle(level) {
  return LEVEL_COLORS[(level || '').toLowerCase()] || { bg: '#f3f4f6', color: '#374151' };
}

function formatDuration(mins) {
  if (!mins) return null;
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

// Gradient fallback backgrounds based on product
function getGradient(products = []) {
  const first = (products[0] || '').toLowerCase();
  if (first.includes('azure'))          return 'linear-gradient(135deg, #0078d4 0%, #005a9e 100%)';
  if (first.includes('microsoft-365') || first.includes('m365'))
                                        return 'linear-gradient(135deg, #d83b01 0%, #b83300 100%)';
  if (first.includes('power'))          return 'linear-gradient(135deg, #742774 0%, #9c27b0 100%)';
  if (first.includes('dynamics'))       return 'linear-gradient(135deg, #002050 0%, #004e8c 100%)';
  if (first.includes('sql') || first.includes('data'))
                                        return 'linear-gradient(135deg, #0e7c7b 0%, #065f5e 100%)';
  if (first.includes('security') || first.includes('defender'))
                                        return 'linear-gradient(135deg, #1b1b1b 0%, #3a3a3a 100%)';
  return 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)';
}

export default function ContentCard({ item, variant = 'module', onSave }) {
  const navigate = useNavigate();

  if (!item) return null;

  const isPath     = variant === 'path';
  const isCustom   = variant === 'custom';
  const gradient   = getGradient(item.products || []);
  const duration   = formatDuration(item.duration_in_minutes);
  const summary    = stripHtml(item.summary);
  const rating     = item.rating?.average;
  const ratingCount = item.rating?.count;

  const handleClick = () => {
    if (isCustom) { navigate(`/create-path/${item.id}`); return; }
    const uid = encodeURIComponent(item.uid || item.id || '');
    navigate(isPath ? `/learning-paths/${uid}` : `/modules/${uid}`);
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardActionArea onClick={handleClick} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {/* Card Image / Gradient header */}
        <Box sx={{ position: 'relative', paddingTop: '52%', overflow: 'hidden', flexShrink: 0 }}>
          {item.social_image_url ? (
            <CardMedia
              component="img"
              image={item.social_image_url}
              alt={item.title}
              sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                background: gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {item.icon_url ? (
                <Box
                  component="img"
                  src={item.icon_url}
                  alt=""
                  sx={{ width: 64, height: 64, filter: 'brightness(0) invert(1)', opacity: 0.85 }}
                />
              ) : isPath ? (
                <RouteIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.8)' }} />
              ) : (
                <SchoolIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.8)' }} />
              )}
            </Box>
          )}

          {/* Type badge */}
          <Box
            sx={{
              position: 'absolute', top: 10, left: 10,
              backgroundColor: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(6px)',
              borderRadius: '6px',
              px: 1, py: 0.25,
            }}
          >
            <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {isCustom ? 'My Path' : isPath ? 'Learning Path' : 'Module'}
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Level chip */}
          {(item.levels || []).length > 0 && (
            <Chip
              label={(item.levels[0]).charAt(0).toUpperCase() + item.levels[0].slice(1)}
              size="small"
              sx={{
                ...getLevelStyle(item.levels[0]),
                fontSize: '0.7rem',
                fontWeight: 600,
                height: 20,
                mb: 1,
                alignSelf: 'flex-start',
              }}
            />
          )}

          {/* Title */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: '0.95rem',
              lineHeight: 1.35,
              mb: 0.75,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.title}
          </Typography>

          {/* Summary */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.8rem',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              flex: 1,
              mb: 1.5,
            }}
          >
            {summary}
          </Typography>

          {/* Products */}
          {(item.products || []).length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1.5, gap: 0.5 }}>
              {item.products.slice(0, 3).map((p) => (
                <Chip key={p} label={p} size="small" sx={{ fontSize: '0.65rem', height: 18, backgroundColor: '#f3f4f6', color: '#374151' }} />
              ))}
              {item.products.length > 3 && (
                <Chip label={`+${item.products.length - 3}`} size="small" sx={{ fontSize: '0.65rem', height: 18, backgroundColor: '#f3f4f6', color: '#374151' }} />
              )}
            </Stack>
          )}

          {/* Footer meta */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {duration && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TimerIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem' }}>{duration}</Typography>
              </Stack>
            )}
            {rating && (
              <Tooltip title={`${ratingCount?.toLocaleString()} ratings`}>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <StarIcon sx={{ fontSize: 13, color: '#f59e0b' }} />
                  <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#92400e' }}>{rating.toFixed(1)}</Typography>
                </Stack>
              </Tooltip>
            )}
            {isPath && item.number_of_children && (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem' }}>
                {item.number_of_children} modules
              </Typography>
            )}
            {isCustom && (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem' }}>
                {(item.modules || []).length} modules
              </Typography>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
